import { createAuth0, type Auth0Plugin, type Auth0VueClient } from '@auth0/auth0-vue';

import { useAuth0Client } from '@/composables/useAuth0Client';
import { isServer } from '@/helpers/Platform/platform';

let auth0Instance: Auth0Plugin | undefined;

export const createAuth0Instance = (): Auth0Plugin | undefined => {
  const audience = import.meta.env.VUE_APP_AUTH0_AUDIENCE;
  const clientId = import.meta.env.VUE_APP_AUTH0_CLIENT_ID;
  const domain = import.meta.env.VUE_APP_AUTH0_DOMAIN;

  if (isServer() || !audience || !clientId || !domain) {
    return undefined;
  }

  // This app only reads tokens silently, so keep the callback at the origin root.
  // Adding app-specific subpaths would require extra Auth0 allow-list entries.
  const redirectUri = `${window.location.origin}/login-callback`;

  auth0Instance ??= createAuth0(
    {
      domain,
      clientId,
      authorizationParams: {
        audience,
        redirect_uri: redirectUri,
      },
      cookieDomain: import.meta.env.VUE_APP_COOKIE_DOMAIN,
      cacheLocation: 'memory',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      sessionCheckExpiryDays: 365,
    },
    {
      skipRedirectCallback: true,
    },
  );

  return auth0Instance;
};

const isExpectedSilentAuthError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('error' in error)) {
    return false;
  }

  return ['consent_required', 'login_required', 'missing_refresh_token', 'timeout'].includes(
    String(error.error),
  );
};

const tryGetTokenSilently = async (client: Auth0VueClient): Promise<string | undefined> => {
  try {
    return await client.getAccessTokenSilently();
  } catch (error) {
    if (!isExpectedSilentAuthError(error)) {
      throw error;
    }
  }
};

export const getAuthToken = async (
  auth0Client: Auth0VueClient | null = useAuth0Client(),
): Promise<string | undefined> => {
  const client = auth0Client;
  if (!client) {
    return undefined;
  }

  return await tryGetTokenSilently(client);
};
